import { useState } from "react";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { Col, Row } from "react-bootstrap";

import Card from "../../../../components/core/basic/Card";
import { WalmartAdvertisingSetup } from "../../../../modules/onboarding/walmart/WalmartAdvertisingSetup";
import ShopifySetup from "../../../../modules/shopify/components/ShopifySetup";
import { Button } from "../../../../components/core/basic/Button";
import { useAppsStatus } from "../../../../modules/admin/api/useAppsStatus";
import Loading from "../../../../components/core/blocks/Loading";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import { useTranslation } from "react-i18next";

const Apps = () => {
  const { t } = useTranslation(["admin"]);
  const appsStatusQuery = useAppsStatus({ refetchInterval: 30000 });
  const [showWalmartAdvertiserSetup, setShowWalmartAdvertiserSetup] = useState(false);
  const [showShopifySetup, setShowShopifySetup] = useState(false);

  const navigate = useNavigate();

  const getStatusText = (status: string) => {
    switch (status) {
      case "not_connected":
        return t(($) => $.admin["Not Connected"]);
      case "connected":
        return t(($) => $.admin["Connected"]);
      case "loading":
        return t(($) => $.admin["Loading..."]);
      default:
        return t(($) => $.admin["Waiting Approval"]);
    }
  };

  if (appsStatusQuery.isLoading) {
    return <Loading fullPage />;
  }

  if (!appsStatusQuery.isSuccess) {
    return <div>{t(($) => $.admin["Failed to load apps status"])}</div>;
  }

  return (
    <>
      <PageContainer paddingX="xxlarge">
        <Row>
          <Col className={"s8 offset-s2 text-center"}>
            <h4>
              <b>{t(($) => $.admin["Trellis Connect Apps"])}</b>
            </h4>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card>
              <Row style={{ margin: "20px" }} className="fs-4">
                <Col xs={12}>
                  <p className="text-center" style={{ marginBottom: "50px" }}>
                    {t(
                      ($) =>
                        $.admin[
                          "On the Trellis Connect Apps screen you can enable various apps to unlock new features."
                        ],
                    )}
                  </p>
                  <Row style={{ paddingBottom: "15px" }}>
                    <Col xs={6}>
                      <strong>{t(($) => $.admin["App"])}</strong>
                    </Col>
                    <Col className={"text-center"} xs={3}>
                      <strong>{t(($) => $.admin["Connect"])}</strong>
                    </Col>
                    <Col xs={3}>&nbsp;</Col>
                  </Row>
                  <Row
                    style={{
                      paddingBottom: "10px",
                      marginBottom: "10px",
                      borderBottom: "2px solid #ddd",
                    }}
                  >
                    <Col style={{ paddingTop: "10px" }} xs={6}>
                      {t(($) => $.admin["Amazon Advertising"])}
                    </Col>
                    <Col className={"text-center"} style={{ paddingTop: "10px" }} xs={3}>
                      {getStatusText(appsStatusQuery.data.amazon.status)}
                    </Col>
                    <Col className={"text-center"} xs={3}>
                      <Button variant={"secondary"} onPress={() => void navigate({ to: "/user/settings" })}>
                        {t(($) => $.admin["Settings"])}
                      </Button>
                    </Col>
                  </Row>
                  <Row
                    style={{
                      paddingBottom: "10px",
                      marginBottom: "10px",
                      borderBottom: "2px solid #ddd",
                    }}
                  >
                    <Col style={{ paddingTop: "10px" }} xs={6}>
                      {t(($) => $.admin["Amazon Connect"])}
                    </Col>
                    <Col className={"text-center"} style={{ paddingTop: "10px" }} xs={3}>
                      {getStatusText(appsStatusQuery.data.amazon_mws.status)}
                    </Col>
                    <Col className={"text-center"} xs={3}>
                      {appsStatusQuery.data.amazon_mws.status === "connected" ?
                        <Button variant="secondary" onPress={() => void navigate({ to: "/user/amzsummary" })}>
                          {t(($) => $.admin["Preferences"])}
                        </Button>
                      : <Button onPress={() => void navigate({ to: "/user/amzsummary" })}>
                          {t(($) => $.admin["Connect"])}
                        </Button>
                      }
                    </Col>
                  </Row>

                  <Row
                    style={{
                      paddingBottom: "10px",
                      marginBottom: "10px",
                      borderBottom: "2px solid #ddd",
                    }}
                  >
                    <Col style={{ paddingTop: "10px" }} xs={6}>
                      {t(($) => $.admin["Walmart Advertising"])}
                    </Col>
                    <Col className={"text-center"} style={{ paddingTop: "10px" }} xs={3}>
                      {getStatusText(appsStatusQuery.data["wm-advertising"].status)}
                    </Col>
                    <Col className={"text-center"} xs={3}>
                      {showWalmartAdvertiserSetup ?
                        <WalmartAdvertisingSetup
                          open={showWalmartAdvertiserSetup}
                          onClose={() => {
                            void appsStatusQuery.refetch();
                            setShowWalmartAdvertiserSetup(false);
                          }}
                        />
                      : null}
                      <Button onPress={() => setShowWalmartAdvertiserSetup(true)}>{t(($) => $.admin["Setup"])}</Button>
                    </Col>
                  </Row>

                  <Row
                    style={{
                      paddingBottom: "10px",
                      marginBottom: "10px",
                      borderBottom: "2px solid #ddd",
                    }}
                  >
                    <Col style={{ paddingTop: "10px" }} xs={6}>
                      {t(($) => $.admin["Walmart Marketplace"])}
                    </Col>
                    <Col className={"text-center"} style={{ paddingTop: "10px" }} xs={3}>
                      {appsStatusQuery.data["wm-mp"].is_connected ?
                        appsStatusQuery.data["wm-mp"].auth_method === "oauth" ?
                          t(($) => $.admin["Connected (OAuth)"])
                        : t(($) => $.admin["Connected (Legacy)"])
                      : t(($) => $.admin["Not Connected"])}
                    </Col>
                    <Col className={"text-center"} xs={3}>
                      {appsStatusQuery.data["wm-mp"].is_connected ?
                        <Button variant="secondary" onPress={() => void navigate({ to: "/user/walmartsummary" })}>
                          {t(($) => $.admin["Preferences"])}
                        </Button>
                      : <Button onPress={() => void navigate({ to: "/user/walmartsummary" })}>
                          {t(($) => $.admin["Connect"])}
                        </Button>
                      }
                    </Col>
                  </Row>

                  <Row
                    style={{
                      paddingBottom: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    <Col style={{ paddingTop: "10px" }} xs={6}>
                      {t(($) => $.admin["Shopify"])}
                      <div>
                        <small>
                          <a
                            href="https://trellis-website.s3.us-west-2.amazonaws.com/Shopify+Install.pdf"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t(($) => $.admin["Instructions on how to setup Shoppify App"])}
                          </a>
                        </small>
                      </div>
                    </Col>
                    <Col className={"text-center"} style={{ paddingTop: "10px" }} xs={3}>
                      {getStatusText(appsStatusQuery.data.shopify.status)}
                    </Col>
                    <Col className={"text-center"} xs={3}>
                      {appsStatusQuery.data.shopify.status === "connected" ?
                        <Button variant={"secondary"} isDisabled>
                          {t(($) => $.admin["Connected"])}
                        </Button>
                      : <Button onPress={() => setShowShopifySetup(true)}>{t(($) => $.admin["Connect"])}</Button>}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </PageContainer>
      <ShopifySetup is_visible={showShopifySetup} onHide={() => setShowShopifySetup(false)} />
    </>
  );
};

export const Route = createFileRoute("/_authenticated/user/apps/")({
  component: Apps,
  staticData: { application: "Admin" },
});
